/**
 * Error translation utility
 * Maps Supabase and Zod errors to Swedish translations
 */

import { translations } from '@/lib/i18n/translations';

/**
 * Translate error message to Swedish
 * Handles Supabase auth errors, Zod validation errors, and common error patterns
 */
export function translateError(error: any, language: 'sv' | 'en' = 'sv'): string {
  if (!error) {
    return translations[language].auth.unknownError;
  }

  const errorMessage = typeof error === 'string' ? error : error.message || '';
  const errorCode = error?.code || error?.status || '';
  const lowerMessage = errorMessage.toLowerCase();

  // Handle Zod validation errors
  if (error.issues && Array.isArray(error.issues)) {
    const firstIssue = error.issues[0];
    if (firstIssue?.message) {
      // Check if message is already in Swedish
      if (language === 'sv' && firstIssue.message.includes('måste')) {
        return firstIssue.message;
      }
      // Translate common Zod messages
      if (firstIssue.message.includes('email') || firstIssue.message.includes('Invalid email')) {
        return translations[language].auth.invalidEmail;
      }
      if (firstIssue.message.includes('password') || firstIssue.message.includes('min')) {
        return translations[language].auth.passwordTooShort;
      }
      return firstIssue.message;
    }
  }

  // Handle Supabase auth errors
  if (errorCode || lowerMessage) {
    // Email not confirmed
    if (
      errorCode === 'email_not_confirmed' ||
      lowerMessage.includes('email not confirmed') ||
      lowerMessage.includes('e-postadressen är inte bekräftad')
    ) {
      return translations[language].auth.emailNotConfirmed;
    }

    // Invalid email
    if (
      errorCode === 'invalid_email' ||
      lowerMessage.includes('invalid email') ||
      lowerMessage.includes('ogiltig e-post')
    ) {
      return translations[language].auth.invalidEmail;
    }

    // Email logins disabled
    if (
      errorCode === 'email_logins_disabled' ||
      lowerMessage.includes('email logins are disabled') ||
      lowerMessage.includes('email login is disabled')
    ) {
      return 'E-postinloggning är inaktiverad. Vänligen kontakta support.';
    }

    // Email already exists
    if (
      errorCode === 'signup_disabled' ||
      lowerMessage.includes('already registered') ||
      lowerMessage.includes('already exists') ||
      lowerMessage.includes('user already registered') ||
      lowerMessage.includes('finns redan')
    ) {
      return translations[language].auth.emailAlreadyExists;
    }

    // Weak password
    if (
      errorCode === 'weak_password' ||
      lowerMessage.includes('weak password') ||
      lowerMessage.includes('password is too weak')
    ) {
      return translations[language].auth.weakPassword;
    }

    // Password too short
    if (
      lowerMessage.includes('password') &&
      (lowerMessage.includes('short') || lowerMessage.includes('min') || lowerMessage.includes('6'))
    ) {
      return translations[language].auth.passwordTooShort;
    }

    // Invalid credentials
    if (
      errorCode === 'invalid_credentials' ||
      errorCode === 'invalid_grant' ||
      lowerMessage.includes('invalid login') ||
      lowerMessage.includes('invalid credentials') ||
      lowerMessage.includes('wrong password') ||
      lowerMessage.includes('incorrect password')
    ) {
      return translations[language].auth.invalidCredentials;
    }

    // Too many requests
    if (
      errorCode === 'too_many_requests' ||
      lowerMessage.includes('too many requests') ||
      lowerMessage.includes('rate limit')
    ) {
      return translations[language].auth.tooManyRequests;
    }

    // Network errors
    if (
      lowerMessage.includes('network') ||
      lowerMessage.includes('fetch') ||
      lowerMessage.includes('connection') ||
      lowerMessage.includes('timeout')
    ) {
      return translations[language].auth.networkError;
    }

    // Verification failed
    if (
      lowerMessage.includes('verification') ||
      lowerMessage.includes('verify') ||
      lowerMessage.includes('bekräftelse')
    ) {
      return translations[language].auth.verificationFailed;
    }

    // Signup failed
    if (lowerMessage.includes('signup') || lowerMessage.includes('registrering')) {
      return translations[language].auth.signupFailed;
    }

    // Login failed
    if (lowerMessage.includes('login') || lowerMessage.includes('inloggning')) {
      return translations[language].auth.loginFailed;
    }
  }

  // If error message is already in Swedish, return as-is
  if (language === 'sv' && (errorMessage.includes('är') || errorMessage.includes('måste') || errorMessage.includes('inte'))) {
    return errorMessage;
  }

  // Fallback to original message or unknown error
  return errorMessage || translations[language].auth.unknownError;
}

